// src/components/settings/IntegrationsTab.tsx
import React from "react";
import {
  Card,
  Grid,
  Typography,
  Button,
  Box,
  Avatar,
  Divider,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

export interface GmailConfig {
  connected: boolean;
  email: string | null;
  lastSync: string | null;
}

interface IntegrationsTabProps {
  gmailConfig: GmailConfig;
  gmailLoading: boolean;
  onConnectGmail: () => Promise<void>;
  onDisconnectGmail: () => Promise<void>;
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({
  gmailConfig,
  gmailLoading,
  onConnectGmail,
  onDisconnectGmail,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Grid container spacing={3}>
      {/* Gmail Integration */}
      <Grid item xs={12}>
        <Card sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, flex: 1 }}>
              <Avatar
                sx={{
                  backgroundColor: gmailConfig.connected
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.grey[500], 0.1),
                  flexShrink: 0,
                }}
              >
                <GoogleIcon
                  color={gmailConfig.connected ? "success" : "disabled"}
                />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={600}
                >
                  Gmail Integration
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Send emails directly through your Gmail account
                  </Typography>
                )}
              </Box>
            </Box>
            {gmailConfig.connected ? (
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label="Connected"
                color="success"
                sx={{ flexShrink: 0 }}
              />
            ) : (
              <Chip
                size="small"
                label="Not Connected"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            {gmailConfig.connected ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Connected to <strong>{gmailConfig.email}</strong>
                    {gmailConfig.lastSync && (
                      <span>
                        {" "}
                        · Last synced:{" "}
                        {new Date(gmailConfig.lastSync).toLocaleString()}
                      </span>
                    )}
                  </Typography>
                </Alert>
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={isMobile ? 1 : 2}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onDisconnectGmail}
                    disabled={gmailLoading}
                    fullWidth={isMobile}
                  >
                    Disconnect Gmail
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={onConnectGmail}
                    disabled={gmailLoading}
                    fullWidth={isMobile}
                  >
                    Reconnect
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Connect your Gmail account to send listing emails, guest
                    cards, lease emails, and invoices directly from your email
                    address. All outbound messages include open and click
                    tracking.
                  </Typography>
                </Alert>
                <Button
                  variant="contained"
                  startIcon={
                    gmailLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <GoogleIcon />
                    )
                  }
                  onClick={onConnectGmail}
                  disabled={gmailLoading}
                  fullWidth={isMobile}
                  sx={{
                    backgroundColor: "#4285F4",
                    "&:hover": { backgroundColor: "#357ABD" },
                  }}
                >
                  Connect Gmail Account
                </Button>
              </>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Features available with Gmail:
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              <Chip label="Listing Emails" size="small" variant="outlined" />
              <Chip label="Guest Cards" size="small" variant="outlined" />
              <Chip label="Lease Emails" size="small" variant="outlined" />
              <Chip label="Invoices" size="small" variant="outlined" />
              <Chip label="Open & Click Tracking" size="small" variant="outlined" />
            </Stack>
          </Box>
        </Card>
      </Grid>

      {/* Coming Soon Integrations */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, opacity: 0.6 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
              }}
            >
              <StorageIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">CRM Integration</Typography>
              <Chip label="Coming Soon" size="small" />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Connect with popular CRM systems like Salesforce, HubSpot, and more.
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, opacity: 0.6 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
              }}
            >
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">Calendar Sync</Typography>
              <Chip label="Coming Soon" size="small" />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Sync tours and appointments with Google Calendar or Outlook.
          </Typography>
        </Card>
      </Grid>
    </Grid>
  );
};

export default IntegrationsTab;
